import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CategorySlug } from "@mestergo/shared";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";
import { getTelegramWebApp } from "../lib/telegram";

interface HandymanCard {
  id: string;
  headline?: string | null;
  bio?: string | null;
  city?: string | null;
  verificationLevel: "unverified" | "identity_verified" | "pro_verified" | "trusted_pro";
  ratingAvg: string;
  ratingCount: number;
  user: {
    displayName?: string | null;
    firstName?: string | null;
    phone?: string | null;
    telegramUsername?: string | null;
  };
  categories: Array<{ slug: string; nameRo: string; nameRu?: string | null }>;
}

const CATEGORY_SLUGS: CategorySlug[] = [
  CategorySlug.Plumber,
  CategorySlug.Electrician,
  CategorySlug.ApplianceRepair,
  CategorySlug.Cleaning,
  CategorySlug.Moving,
  CategorySlug.Taxi,
  CategorySlug.Other,
];

export function HandymenPage() {
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const [handymen, setHandymen] = useState<HandymanCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<CategorySlug | "">("");
  const [cityFilter, setCityFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const tg = getTelegramWebApp();
    if (!tg) return;
    tg.BackButton.show();
    const onBack = () => navigate("/");
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (categoryFilter) params.set("categorySlug", categoryFilter);
    if (cityFilter.trim()) params.set("city", cityFilter.trim());
    api.get<HandymanCard[]>(`/handymen/public?${params.toString()}`)
      .then(({ data }) => setHandymen(data))
      .finally(() => setLoading(false));
  }, [categoryFilter, cityFilter]);

  const q = searchQuery.toLowerCase().trim();
  const displayedHandymen = q
    ? handymen.filter((h) => {
        const name = (h.user.displayName || h.user.firstName || "").toLowerCase();
        const headline = (h.headline || "").toLowerCase();
        const bio = (h.bio || "").toLowerCase();
        const city = (h.city || "").toLowerCase();
        const cats = h.categories
          .map((c) => (locale === "ru" ? c.nameRu || c.nameRo : c.nameRo).toLowerCase())
          .join(" ");
        return name.includes(q) || headline.includes(q) || bio.includes(q) || city.includes(q) || cats.includes(q);
      })
    : handymen;

  const getCategoryLabel = (slug: string) => {
    const map: Record<string, string> = {
      plumber: t.categories.plumber,
      electrician: t.categories.electrician,
      appliance_repair: t.categories.applianceRepair,
      cleaning: t.categories.cleaning,
      moving: t.categories.moving,
      taxi: t.categories.taxi,
      other: t.categories.other,
    };
    return map[slug] ?? slug;
  };

  const getVerificationLabel = (level: HandymanCard["verificationLevel"]) => {
    switch (level) {
      case "identity_verified": return t.verificationLabels.identityVerified;
      case "pro_verified": return t.verificationLabels.proVerified;
      case "trusted_pro": return t.verificationLabels.trustedPro;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate("/")} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="text-2xl font-bold text-gray-900">{t.handymenTitle}</div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-100 px-5 py-4 space-y-3">
        {/* Keyword search */}
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchHandymen}
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-4 text-sm text-gray-900 outline-none focus:border-orange-400"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg leading-none"
            >
              ×
            </button>
          ) : null}
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          <button
            type="button"
            onClick={() => setCategoryFilter("")}
            className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              categoryFilter === "" ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            {t.allCategories}
          </button>
          {CATEGORY_SLUGS.map((slug) => (
            <button
              key={slug}
              type="button"
              onClick={() => setCategoryFilter(slug === categoryFilter ? "" : slug)}
              className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                categoryFilter === slug ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {getCategoryLabel(slug)}
            </button>
          ))}
        </div>

        {/* City filter */}
        <input
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          placeholder={`📍 ${t.cityPlaceholder}`}
          className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-orange-400"
        />
      </div>

      <div className="px-4 pt-4 pb-6 space-y-2.5">
        {loading ? (
          <div className="py-12 text-center">
            <div className="inline-block w-6 h-6 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : displayedHandymen.length === 0 ? (
          <div className="rounded-2xl bg-white px-5 py-10 text-center shadow-sm">
            <div className="text-3xl mb-2">🔍</div>
            <div className="text-sm text-gray-500">{t.noHandymenFound}</div>
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="mt-3 text-xs font-semibold text-orange-500"
              >
                {t.allCategories} →
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-400 font-medium px-1 pb-0.5">
              {displayedHandymen.length} {t.handymenTitle.toLowerCase()}
            </div>
            {displayedHandymen.map((handyman) => {
              const name = handyman.user.displayName || handyman.user.firstName || "Handyman";
              const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
              const telegramLink = handyman.user.telegramUsername
                ? `https://t.me/${handyman.user.telegramUsername}`
                : null;
              const verificationLabel = getVerificationLabel(handyman.verificationLevel);
              const categoryNames = handyman.categories.map((c) =>
                locale === "ru" ? c.nameRu || c.nameRo : c.nameRo,
              );
              const rating = Number(handyman.ratingAvg);

              return (
                <div key={handyman.id} className="rounded-2xl bg-white shadow-sm overflow-hidden">
                  {/* Tappable card body → profile */}
                  <button
                    type="button"
                    onClick={() => navigate(`/handymen/${handyman.id}`)}
                    className="w-full text-left px-4 pt-3.5 pb-3 active:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-sm font-bold text-orange-600">
                        {initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        {/* Row 1: name + rating */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-gray-900 truncate leading-tight">{name}</div>
                          <div className="shrink-0 text-xs font-bold text-amber-500 flex items-center gap-0.5">
                            ★ {rating.toFixed(1)}
                          </div>
                        </div>
                        {/* Row 2: headline */}
                        {handyman.headline ? (
                          <div className="mt-0.5 text-xs text-gray-500 leading-snug line-clamp-1">{handyman.headline}</div>
                        ) : null}
                        {/* Row 3: city + verification */}
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          {handyman.city ? (
                            <span className="text-[11px] text-gray-400">📍 {handyman.city}</span>
                          ) : null}
                          {verificationLabel ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                              ✓ {verificationLabel}
                            </span>
                          ) : null}
                        </div>
                        {/* Row 4: categories */}
                        {categoryNames.length > 0 ? (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {categoryNames.map((catName) => (
                              <span key={catName} className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-medium text-orange-600">
                                {catName}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </button>

                  {/* Action bar */}
                  <div className="border-t border-gray-100 px-3.5 py-2.5 flex items-center gap-2">
                    {telegramLink ? (
                      <a
                        href={telegramLink}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 rounded-xl bg-orange-500 py-2 text-center text-xs font-semibold text-white active:bg-orange-600 transition-colors"
                      >
                        💬 {t.openTelegram}
                      </a>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => navigate(`/handymen/${handyman.id}`)}
                      className={`rounded-xl border border-gray-200 py-2 text-xs font-semibold text-gray-700 active:bg-gray-50 transition-colors ${telegramLink ? "px-3" : "flex-1"}`}
                    >
                      👤 {t.viewProfile}
                    </button>
                    {handyman.user.phone ? (
                      <a
                        href={`tel:${handyman.user.phone}`}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-sm active:bg-gray-50 transition-colors"
                      >
                        📞
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
